/**
 * Shared color palettes for all canvas node toolbars.
 *
 * All tint palettes derive from the nine `--sticky-*` tokens (tailwind
 * `cohesiveSticky` / PageFoundations §03). Each tint pairs a sticky fill with a
 * spectrum-dark stroke for shapes, sections, and swatch rings.
 *
 * Palette tiers:
 *   CANVAS_COLORS      — Shape fills. swatchHex = stroke; value = sticky fill.
 *   EDGE_COLORS        — Edge / connector lines. value === swatchHex (stroke).
 *   LABEL_COLORS       — Text label colors. Stroke hues + theme "Default".
 *   NOTE_COLORS        — Text note backgrounds. Sticky fills only.
 *   SECTION_COLORS     — Section fills. swatchHex = stroke; value = sticky fill.
 */

export interface CanvasColor {
  name: string;
  /** Hex or CSS var shown in the color swatch circle */
  swatchHex: string;
  /** Value written to Loro node data */
  value: string;
  /**
   * Swatch drawing, the design-system recipe (specs/canvas-toolbars ColorRow):
   * the circle's centre is the light fill tint, its ring the paired dark
   * stroke. When absent the swatch derives both from `swatchHex`.
   */
  swatchFill?: string;
  swatchStroke?: string;
}

/** Canonical sticky tints — order matches showcase STICKY_VARIANTS. */
const STICKY_TINTS = [
  {
    name: "Apricot",
    fill: "var(--sticky-apricot)",
    stroke: "var(--spectrum-orange-dark)",
  },
  {
    name: "Butter",
    fill: "var(--sticky-butter)",
    stroke: "var(--spectrum-yellow-dark)",
  },
  {
    name: "Meadow",
    fill: "var(--sticky-meadow)",
    stroke: "var(--spectrum-green-dark)",
  },
  {
    name: "Sea",
    fill: "var(--sticky-sea)",
    stroke: "var(--spectrum-blue-dark)",
  },
  {
    name: "Orchid",
    fill: "var(--sticky-orchid)",
    stroke: "var(--spectrum-violet-dark)",
  },
  {
    name: "Rose",
    fill: "var(--sticky-rose)",
    stroke: "var(--spectrum-mulberry-dark)",
  },
  {
    name: "Clay",
    fill: "var(--sticky-clay)",
    stroke: "var(--spectrum-brown-dark)",
  },
  {
    name: "Mist",
    fill: "var(--sticky-mist)",
    stroke: "var(--spectrum-indigo-dark)",
  },
  {
    name: "Oat",
    fill: "var(--sticky-oat)",
    stroke: "var(--spectrum-olive-dark)",
  },
] as const;

// Every sticky-derived swatch draws the same way whatever the palette stores:
// tint centre, dark-stroke ring.
const stickyFill = (t: (typeof STICKY_TINTS)[number]): CanvasColor => ({
  name: t.name,
  swatchHex: t.fill,
  value: t.fill,
  swatchFill: t.fill,
  swatchStroke: t.stroke,
});

const stickyFillWithStrokeSwatch = (
  t: (typeof STICKY_TINTS)[number],
): CanvasColor => ({
  name: t.name,
  swatchHex: t.stroke,
  value: t.fill,
  swatchFill: t.fill,
  swatchStroke: t.stroke,
});

const stickyStroke = (t: (typeof STICKY_TINTS)[number]): CanvasColor => ({
  name: t.name,
  swatchHex: t.stroke,
  value: t.stroke,
  swatchFill: t.fill,
  swatchStroke: t.stroke,
});

const BUTTER = STICKY_TINTS[1];
const OAT = STICKY_TINTS[8];

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** Butter — default shape fill. Must match an entry in SHAPE_COLORS. */
export const DEFAULT_SHAPE_COLOR = BUTTER.fill;

/** Matches showcase SHAPE_COLOR_PALETTE `none` — transparent fill swatch. */
export const CANVAS_NO_FILL_VALUE = "transparent";

/** @deprecated Use CANVAS_NO_FILL_VALUE */
export const SHAPE_NO_FILL_VALUE = CANVAS_NO_FILL_VALUE;

export const CANVAS_NO_FILL_COLOR: CanvasColor = {
  name: "No fill",
  swatchHex: "var(--n-90)",
  value: CANVAS_NO_FILL_VALUE,
};

/** @deprecated Use CANVAS_NO_FILL_COLOR */
export const SHAPE_NO_FILL_COLOR = CANVAS_NO_FILL_COLOR;

export const SHAPE_COLORS: CanvasColor[] = [
  ...STICKY_TINTS.map(stickyFillWithStrokeSwatch),
  CANVAS_NO_FILL_COLOR,
];

// ---------------------------------------------------------------------------
// Edges & connectors
// ---------------------------------------------------------------------------

/** Warm neutral — default edge/connector line color. */
export const DEFAULT_EDGE_COLOR = "var(--n-50)";

export const EDGE_COLORS: CanvasColor[] = [
  { name: "Neutral", swatchHex: DEFAULT_EDGE_COLOR, value: DEFAULT_EDGE_COLOR },
  ...STICKY_TINTS.map(stickyStroke),
];

// ---------------------------------------------------------------------------
// Text labels
// ---------------------------------------------------------------------------

export const DEFAULT_LABEL_COLOR = "default";

export const LABEL_COLORS: CanvasColor[] = [
  {
    name: "Default",
    swatchHex: "hsl(var(--foreground))",
    value: DEFAULT_LABEL_COLOR,
  },
  ...STICKY_TINTS.map(stickyStroke),
];

// ---------------------------------------------------------------------------
// Text notes
// ---------------------------------------------------------------------------

/** Butter — default text note background. Must match an entry in NOTE_COLORS. */
export const DEFAULT_NOTE_COLOR = BUTTER.fill;

export const NOTE_COLORS: CanvasColor[] = STICKY_TINTS.map(stickyFill);

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/** Oat — default section fill. Must match an entry in SECTION_COLORS. */
export const DEFAULT_SECTION_COLOR = OAT.fill;

export const SECTION_COLORS: CanvasColor[] = [
  ...STICKY_TINTS.map(stickyFillWithStrokeSwatch),
  CANVAS_NO_FILL_COLOR,
];
